import React, { createContext, useContext } from "react";

/**
 * Transition & Reintegration Process Form
 */
const ACTIVITIES = [
  { n: 1, label: "Supervised Outings (community, shopping, recreation)" },
  { n: 2, label: "Family Visits (on-site / therapeutic)" },
  { n: 3, label: "Home Visits (client returns home temporarily)" },
  { n: 4, label: "Overnight / Extended Home Leave" },
  { n: 5, label: "Vocational / Educational Site Visit" },
];

// Context keeps the hoisted Check component's identity stable across renders
// (defining it inside the form caused inputs to lose focus each keystroke).
const TransitionFormCtx = createContext(null);

const Check = ({ name, label }) => {
  const { formData, setFormData, readOnly } = useContext(TransitionFormCtx);
  return (
    <div className="col-md-4 col-sm-6">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`chk_${name}`}
          checked={!!formData[name]}
          onChange={() => !readOnly && setFormData((prev) => ({ ...prev, [name]: !prev[name] }))}
          disabled={readOnly}
        />
        <label className="form-check-label" htmlFor={`chk_${name}`}>{label}</label>
      </div>
    </div>
  );
};

const TransitionFormForm = ({ formData, setFormData, readOnly = false }) => {
  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <TransitionFormCtx.Provider value={{ formData, setFormData, readOnly }}>
      <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
        <div className="accordion" id="transitionAccordion">
          {/* Transition To */}
          <div className="accordion-item mb-3 shadow-sm rounded">
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#transTo">
                Transition To
              </button>
            </h2>
            <div id="transTo" className="accordion-collapse collapse show">
              <div className="accordion-body pt-3">
                <div className="row g-2 mb-3">
                  <Check name="to_day_rehab" label="Day Rehab" />
                  <Check name="to_home_visit" label="Home Visit" />
                  <Check name="to_other_facility" label="Other Facility" />
                  <Check name="to_opd" label="OPD" />
                  <Check name="to_others" label="Others" />
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="others_specify" value={formData.others_specify || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Others (specify)</label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* A. Graduated Transition Activities */}
          <div className="accordion-item mb-3 shadow-sm rounded">
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#transActivities">
                A. Graduated Transition Activities
              </button>
            </h2>
            <div id="transActivities" className="accordion-collapse collapse">
              <div className="accordion-body pt-3">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "28%" }}>Activity</th>
                        <th>Initiated Date</th>
                        <th>Frequency</th>
                        <th>Accompanied By</th>
                        <th style={{ width: "14%" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ACTIVITIES.map(({ n, label }) => (
                        <tr key={n}>
                          <td className="small">{label}</td>
                          <td><input type="date" className="form-control form-control-sm" name={`act${n}_initiated_date`} value={formData[`act${n}_initiated_date`] || ""} onChange={handleChange} disabled={readOnly} /></td>
                          <td><input type="text" className="form-control form-control-sm" name={`act${n}_frequency`} value={formData[`act${n}_frequency`] || ""} onChange={handleChange} disabled={readOnly} /></td>
                          <td><input type="text" className="form-control form-control-sm" name={`act${n}_accompanied_by`} value={formData[`act${n}_accompanied_by`] || ""} onChange={handleChange} disabled={readOnly} /></td>
                          <td>
                            <select className="form-select form-select-sm" name={`act${n}_status`} value={formData[`act${n}_status`] || ""} onChange={handleChange} disabled={readOnly}>
                              <option value="">Select</option>
                              <option value="Pending">Pending</option>
                              <option value="Active">Active</option>
                              <option value="Complete">Complete</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* B. Discharge Preparation */}
          <div className="accordion-item mb-3 shadow-sm rounded">
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#transDischarge">
                B. Discharge Preparation
              </button>
            </h2>
            <div id="transDischarge" className="accordion-collapse collapse">
              <div className="accordion-body pt-3">
                <div className="row g-2 mb-3">
                  <Check name="disch_criteria_reviewed" label="Discharge criteria reviewed with client and family" />
                  <Check name="disch_relapse_prevention" label="Relapse prevention plan completed (WRAP or equivalent)" />
                  <Check name="disch_community_support" label="Community support services identified and linked" />
                  <Check name="disch_medication_plan" label="Medication management plan confirmed" />
                  <Check name="disch_crisis_contacts" label="Crisis contacts established" />
                  <Check name="disch_followup_appointments" label="Outpatient/follow-up appointments scheduled" />
                  <Check name="disch_housing_confirmed" label="Housing confirmed and safety-assessed" />
                  <Check name="disch_family_orientation" label="Family orientation and briefing conducted" />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                      <input type="date" className="form-control" name="planned_discharge_date" value={formData.planned_discharge_date || ""} onChange={handleChange} disabled={readOnly} />
                      <label>Planned Discharge Date</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                      <input type="date" className="form-control" name="reintegration_date" value={formData.reintegration_date || ""} onChange={handleChange} disabled={readOnly} />
                      <label>Reintegration Date</label>
                    </div>
                  </div>
                </div>

                <div className="form-floating form-floating-outline">
                  <textarea className="form-control" style={{ height: "110px" }} name="reintegration_notes" value={formData.reintegration_notes || ""} onChange={handleChange} disabled={readOnly}></textarea>
                  <label>Reintegration Notes</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TransitionFormCtx.Provider>
  );
};

export default TransitionFormForm;
